/**
 * PORT-005 §250 — **la coque entre SEULE quand il n'y a rien à choisir.**
 *
 * « Le shell lit `/me` : 1 contexte → `select` automatique ;
 * `default_context_id` non nul → `select` automatique ; sinon `/espace/choisir`. »
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 CE QUI MANQUAIT — trouvé le 2026-09-15 en suivant le parcours du PO
 * ═══════════════════════════════════════════════════════════════════════════
 * Trois textes affirmaient que « le serveur sélectionne le contexte à
 * l'entrée ». Aucune ligne ne le faisait — la connexion n'écrit volontairement
 * rien, cf. `api/portal/login.py`. La coque envoyait donc une personne à UN
 * seul espace sur une page de choix à une ligne. Et le défaut mémorisé
 * (« Ouvrir cet espace par défaut ») était enregistré par le serveur et relu
 * par AUCUN écran.
 *
 * ⭐ Pourquoi ici, et pas à la connexion : une personne entre par QUATRE portes
 * (mot de passe, double authentification, Google/Apple, portail central). Les
 * quatre mènent à la coque ; poser la sélection dans chacune serait écrire la
 * même règle quatre fois — et ce dépôt a déjà payé un verrou posé sur trois
 * portes sur quatre.
 *
 * ⚠️ Deux garde-fous, chacun éprouvé par
 * `__tests__/laCoqueEntreSeuleDansSonUniqueEspace.test.tsx` :
 *  · un défaut qui ne désigne plus un espace servi est IGNORÉ ;
 *  · une entrée REFUSÉE (accès fermé entre deux lectures) ne se rejoue pas :
 *    on laisse la personne choisir, sinon la coque bouclerait sur un 404.
 *
 * 🔴 Et un troisième (A12, arbitrage PO du 2026-09-16) : **un espace fermé PENDANT
 * la visite n'ouvre rien seul.** `/me` sert alors `active_context_closed`, et la
 * coque entrait dans l'espace restant — ou dans le défaut — sans un mot : la
 * personne changeait d'espace sans savoir pourquoi. Elle passe désormais par la
 * page de choix, qui dit `portal.context.closed`. Éprouvé par
 * `__tests__/lEtatVideEtLEspaceFermeNeTournentPasEnRond.test.tsx`.
 */
import { useEffect, useRef, useState } from 'react'

import { SANS_SESSION } from '@/hooks/usePortalMe'
import type { PortalMe } from '@/hooks/usePortalMe.types'

import { useChoisirLEspace } from './useChoisirLEspace'

/** L'espace dans lequel entrer sans rien demander — ou `null` s'il faut choisir. */
export function quelEspaceOuvrirSeul(
    data: PortalMe | typeof SANS_SESSION | undefined,
): string | null {
    if (!data || data === SANS_SESSION || data.active_context_id) return null
    if (data.active_context_closed) return null
    if (data.contexts.length === 1) return data.contexts[0].id
    const defaut = data.default_context_id
    if (defaut && data.contexts.some((c) => c.id === defaut)) return defaut
    return null
}

/**
 * Lance l'entrée automatique et rend `true` tant qu'elle est EN COURS — la
 * coque montre alors son squelette plutôt qu'une page de choix fugace.
 * Rend `false` quand il faut choisir, y compris après un refus.
 */
export function useEntreeAutomatique(
    data: PortalMe | typeof SANS_SESSION | undefined,
): boolean {
    // `remplacer` : entrer seul ne doit pas laisser dans l'historique une étape
    // que la personne n'a jamais vue.
    const entrer = useChoisirLEspace({ remplacer: true })
    const cible = quelEspaceOuvrirSeul(data)
    // ⚠️ Un REF, pas un état : `entrer` change d'identité à chaque rendu, et
    // l'effet se relance donc souvent. Seule la mémoire des tentatives empêche
    // de poster deux fois la même entrée.
    const tentees = useRef(new Set<string>())
    const [refusees, setRefusees] = useState<ReadonlySet<string>>(() => new Set())

    useEffect(() => {
        if (!cible || tentees.current.has(cible)) return
        tentees.current.add(cible)
        void entrer(cible).then((ouvert) => {
            if (!ouvert) setRefusees((avant) => new Set(avant).add(cible))
        })
    }, [cible, entrer])

    return cible !== null && !refusees.has(cible)
}
